/**
 * Platform detection utilities for web, PWA, and native apps
 */

export type Platform = 'web' | 'pwa' | 'capacitor' | 'react-native'

/**
 * Detects the current platform the app is running on
 */
export function detectPlatform(): Platform {
  // Check if running in Capacitor (native iOS/Android)
  if (typeof window !== 'undefined' && (window as any).Capacitor) {
    return 'capacitor'
  }
  
  // Check if running in React Native
  if (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') {
    return 'react-native'
  }
  
  // Check if running as PWA (installed)
  if (typeof window !== 'undefined') {
    // Check display mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches
    
    // Check if launched from home screen (iOS)
    const isIOSStandalone = (navigator as any).standalone === true
    
    if (isStandalone || isFullscreen || isIOSStandalone) {
      return 'pwa'
    }
  }
  
  // Default to web browser
  return 'web'
}

/**
 * Check if the platform supports native downloads (no CORS restrictions)
 */
export function supportsNativeDownloads(): boolean {
  const platform = detectPlatform()
  return platform === 'capacitor' || platform === 'react-native'
}

/**
 * Check if the platform is running in a browser context
 */
export function isBrowserContext(): boolean {
  const platform = detectPlatform()
  return platform === 'web' || platform === 'pwa'
}

/**
 * Get platform-specific capabilities
 */
export function getPlatformCapabilities() {
  const platform = detectPlatform()
  
  return {
    platform,
    supportsNativeDownloads: supportsNativeDownloads(),
    isBrowserContext: isBrowserContext(),
    needsProxy: isBrowserContext(), // Browser contexts need proxy for CORS
    supportsFileSystem: platform === 'capacitor' || platform === 'react-native',
    supportsServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
  }
}
